import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, PenLine } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { getMatchesByTournamentId, updateMatchResult } from "services/matchService";
import { canManage } from "services/tournamentService";
import { useAuth } from "context/AuthProvider";
import ScoreEditModal from "./scoreEditModal";
import * as Sentry from "@sentry/react";

const MyMatches = () => {
  const { user, loading: authLoading } = useAuth();
  const { tournamentId } = useParams();
  const [tournament, setTournament] = useState(null);
  const [location, setLocation] = useState(null);
  const [matches, setMatches] = useState([]);
  const [myMatches, setMyMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMatchId, setEditingMatchId] = useState(null);
  const [editingScores, setEditingScores] = useState({});
  const [swappedMatches, setSwappedMatches] = useState({});
  const [qualificationOpen, setQualificationOpen] = useState(true);
  const [eliminationOpen, setEliminationOpen] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const fetchTournament = async () => {
      try {
        const snapshot = await getDoc(doc(db, "tournaments", tournamentId));
        if (snapshot.exists()) {
          const tournamentData = { id: snapshot.id, ...snapshot.data() };
          setTournament(tournamentData);

          // Fetch location to check who can manage it
          if (tournamentData.locationId) {
            const locationSnapshot = await getDoc(doc(db, "locations", tournamentData.locationId));
            if (locationSnapshot.exists()) {
              setLocation({ id: locationSnapshot.id, ...locationSnapshot.data() });
            }
          }
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error("Erreur lors du chargement du tournoi :", error);
      }
    };

    fetchTournament();
  }, [tournamentId]);

  useEffect(() => {
    if (user && location) {
      setIsAdmin(canManage(user, location.id));
    }
  }, [user, location]);

  useEffect(() => {
    const fetchMatches = async () => {
      if (!tournament?.id || !user?.uid) {
        setLoading(false);
        return;
      }

      try {
        const allMatches = await getMatchesByTournamentId(tournament.id);
        setMatches(allMatches);

        // Filter matches where the user is participating
        const userMatches = allMatches.filter((match) => {
          const isInTeam1 = match.team1?.players?.some(p => p.id === user.uid);
          const isInTeam2 = match.team2?.players?.some(p => p.id === user.uid);
          return isInTeam1 || isInTeam2;
        });

        // Sort matches by advancement level (earliest first, latest last)
        const sortedUserMatches = userMatches.sort((a, b) =>
          getMatchAdvancementLevel(a) - getMatchAdvancementLevel(b)
        );

        setMyMatches(sortedUserMatches);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Erreur lors du chargement des matchs :", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [tournament?.id, user?.uid]);

  const getTeamDisplayName = (match, teamKey) => {
    if (match[teamKey]?.players) {
      return match[teamKey].players.map((p) => p.name).join(" & ");
    }
    return match[`${teamKey}Name`] || "BYE";
  };

  const isUserOnTeam = (match, teamKey) => {
    return match[teamKey]?.players?.some(p => p.id === user.uid);
  };

  const getTournamentDisplayInfo = () => {
    if (tournament?.date) {
      return new Date(tournament.date).toLocaleDateString("fr-FR", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }
    return tournament?.name || tournament?.type || "Tournoi";
  };

  const getMatchTypeTag = (match) => {
    if (match.phase === "elimination") {
      return { text: "Élimination", color: "bg-red-600" };
    } else if (match.phase === "crosspool" || match.phase === "group") {
      return { text: "Qualification", color: "bg-green-600" };
    }
    return { text: "Match", color: "bg-blue-600" };
  };

  const isMatchReadyForScoring = (match) => {
    // For qualification matches (group/crosspool), teams are always present
    if (match.phase === "group" || match.phase === "crosspool") {
      return true;
    }

    // For elimination matches, check if both teams are present (not "?" or placeholder text)
    const team1Ready = match.team1Name &&
                      match.team1Name !== "?" &&
                      !match.team1Name.includes("Winner") &&
                      !match.team1Name.includes("Loser") &&
                      !match.team1Name.includes("Perdant") &&
                      match.team1Name !== "BYE";

    const team2Ready = match.team2Name &&
                      match.team2Name !== "?" &&
                      !match.team2Name.includes("Winner") &&
                      !match.team2Name.includes("Loser") &&
                      !match.team2Name.includes("Perdant") &&
                      match.team2Name !== "BYE";

    return team1Ready && team2Ready;
  };

  const isNextRoundPlayed = (match) => {
    if (match.phase !== "elimination" || !match.nextMatchId) {
      return false;
    }

    // Find the next match and check if it has a result
    const nextMatch = matches.find(m => m.id === match.nextMatchId);
    return nextMatch && nextMatch.result;
  };

  const getMatchButtonState = (match) => {
    if (tournament?.compiled) {
      return { disabled: true, text: "Tournoi terminé", reason: "compiled" };
    }

    // Check for BYE matches
    if (match.team1Name === "BYE" || match.team2Name === "BYE") {
      return { disabled: true, text: "BYE", reason: "bye" };
    }

    // Check if next round is already played (for elimination matches)
    if (isNextRoundPlayed(match)) {
      return { disabled: true, text: "Tour suivant joué", reason: "next_round_played" };
    }

    if (!isMatchReadyForScoring(match)) {
      return { disabled: true, text: "En attente des équipes", reason: "waiting" };
    }

    // Check if qualification phase is over by looking for elimination matches
    if ((match.phase === "group" || match.phase === "crosspool") &&
        myMatches.some(m => m.phase === "elimination")) {
      return { disabled: true, text: "Phase terminée", reason: "phase_over" };
    }

    // Non-admin users cannot edit existing scores
    if (match.result && !isAdmin) {
      return { disabled: true, text: "Score déjà entré", reason: "score_exists" };
    }

    return { disabled: false, text: match.result ? "Modifier score" : "Entrer score", reason: "ready" };
  };

  const groupMatchesByPhase = (matches) => {
    const qualification = matches.filter(m => m.phase === "group" || m.phase === "crosspool");
    const elimination = matches.filter(m => m.phase === "elimination");
    return { qualification, elimination };
  };

  const getMatchAdvancementLevel = (match) => {
    // Lower numbers = earlier matches, higher numbers = later matches
    if (match.phase === "group") return 1;
    if (match.phase === "crosspool") return 2;
    if (match.phase === "elimination") {
      switch (match.round) {
        case "round_of_64": return 3;
        case "round_of_32": return 4;
        case "round_of_16": return 5;
        case "round_of_8": return 3;    // Earlier than quarterfinals
        case "quarterfinal": return 4;
        case "semifinal": return 5;
        case "third_place": return 6;
        case "final": return 7;
        default: return 4;
      }
    }
    return 0;
  };

  const getRoundDisplayName = (round) => {
    const roundNames = {
      "final": "Finale",
      "semifinal": "Demi-finale",
      "quarterfinal": "Quart de finale",
      "round_of_8": "8e de finale",
      "round_of_16": "16e de finale",
      "round_of_32": "32e de finale",
      "round_of_64": "64e de finale",
      "third_place": "Troisième place",
      "first_round": "Premier tour"
    };
    return roundNames[round] || round?.replaceAll("_", " ") || "Round";
  };

  const handleScoreChange = (matchId, teamKey, value) => {
    const match = matches.find(m => m.id === matchId);

    if (tournament?.compiled) {
      alert("❌ Ce tournoi a déjà été compilé. Vous ne pouvez plus modifier les scores.");
      return;
    }

    if (match && isNextRoundPlayed(match)) {
      alert("❌ Impossible de modifier le score. Le tour suivant a déjà été joué.");
      return;
    }

    // If teams are swapped for display, swap the teamKey back to match the actual match data
    const isSwapped = swappedMatches[matchId];
    const actualTeamKey = isSwapped ? (teamKey === "team1" ? "team2" : "team1") : teamKey;

    setEditingScores((prev) => {
      const current = prev[matchId] || {
        team1: matches.find((m) => m.id === matchId)?.result?.team1 ?? 0,
        team2: matches.find((m) => m.id === matchId)?.result?.team2 ?? 0,
      };
      return {
        ...prev,
        [matchId]: {
          ...current,
          [actualTeamKey]: Math.max(0, parseInt(value, 10) || 0),
        },
      };
    });
  };

  const handleScoreSubmit = async (matchId) => {
    const fallbackMatch = matches.find((m) => m.id === matchId);

    if (tournament?.compiled) {
      alert("❌ Ce tournoi a déjà été compilé. Vous ne pouvez plus modifier les scores.");
      return;
    }

    if (fallbackMatch && isNextRoundPlayed(fallbackMatch)) {
      alert("❌ Impossible de modifier le score. Le tour suivant a déjà été joué.");
      return;
    }

    // Non-admin users cannot edit existing scores
    if (fallbackMatch?.result && !isAdmin) {
      alert("❌ Un score a déjà été entré pour ce match. Seuls les administrateurs peuvent modifier les scores existants.");
      return;
    }

    const result = {
      team1: editingScores[matchId]?.team1 ?? fallbackMatch?.result?.team1 ?? "",
      team2: editingScores[matchId]?.team2 ?? fallbackMatch?.result?.team2 ?? "",
    };

    if (!isNaN(result.team1) && !isNaN(result.team2)) {
      try {
        const newResult = result.team1 === 0 && result.team2 === 0 ? null : result;
        await updateMatchResult(matchId, newResult);

        // Refetch all matches to get the latest state (including any team advancements)
        const refreshedMatches = await getMatchesByTournamentId(tournament.id);
        setMatches(refreshedMatches);

        // Re-filter and update myMatches with the refreshed data
        const userMatches = refreshedMatches.filter((match) => {
          const isInTeam1 = match.team1?.players?.some(p => p.id === user.uid);
          const isInTeam2 = match.team2?.players?.some(p => p.id === user.uid);
          return isInTeam1 || isInTeam2;
        });

        const sortedUserMatches = userMatches.sort((a, b) =>
          getMatchAdvancementLevel(a) - getMatchAdvancementLevel(b)
        );

        setMyMatches(sortedUserMatches);

        setEditingMatchId(null);
        setEditingScores((prev) => {
          const updated = { ...prev };
          delete updated[matchId];
          return updated;
        });
        setSwappedMatches((prev) => {
          const updated = { ...prev };
          delete updated[matchId];
          return updated;
        });
      } catch (error) {
        Sentry.captureException(error);
        console.error("Erreur lors de la mise à jour du score :", error);
        alert("❌ Impossible d'enregistrer le score.");
      }
    } else {
      alert("⚠️ Veuillez saisir un score valide pour les deux équipes.");
    }
  };

  const renderMatchGroup = (title, matches, isOpen, toggleOpen, icon) => {
    if (matches.length === 0) return null;

    return (
      <div>
        <button
          onClick={toggleOpen}
          className="flex items-center justify-between w-full bg-slate-800 hover:bg-slate-900 rounded-lg px-3 py-2 transition-colors"
        >
          <div className="flex items-center gap-2">
            <span className="text-base">{icon}</span>
            <h3 className="text-base font-bold text-white">{title}</h3>
            <span className="text-sm bg-slate-900 px-1.5 py-0.5 rounded text-slate-300">
              {matches.length} match{matches.length > 1 ? 's' : ''}
            </span>
          </div>
          <span className="text-white text-base">
            {isOpen ? '▼' : '▶'}
          </span>
        </button>

        {isOpen && (
          <div className="space-y-3 mt-2">
            {matches.map((match) => (
              <div
                key={match.id}
                className="bg-slate-900 border border-slate-700 rounded-lg p-3 shadow-lg"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-1.5 mb-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded text-white ${getMatchTypeTag(match).color}`}>
                        {getMatchTypeTag(match).text}
                      </span>
                      {match.round && match.phase === "elimination" && (
                        <span className="text-xs bg-purple-600 px-1.5 py-0.5 rounded">
                          {getRoundDisplayName(match.round)}
                        </span>
                      )}
                      {match.division && match.division !== "merged" && match.division !== "final" && match.division !== "third_place" && match.division !== "crosspool" && match.phase !== "elimination" && (
                        <span className="text-xs bg-slate-900 px-1.5 py-0.5 rounded">
                          {match.division === "division1" ?
                            (tournament?.makeDivisions ? "Division 1" : "Groupe 1") :
                           match.division === "division2" ?
                            (tournament?.makeDivisions ? "Division 2" : "Groupe 2") :
                           match.division}
                        </span>
                      )}
                    </div>
                    {(() => {
                      const swapped = isUserOnTeam(match, "team2");
                      const left = swapped ? "team2" : "team1";
                      const right = swapped ? "team1" : "team2";
                      return (
                        <>
                          <div className="text-base font-semibold">
                            {getTeamDisplayName(match, left)} vs {getTeamDisplayName(match, right)}
                          </div>
                          <div className="text-slate-300 mt-1">
                            Résultat: {match.result ? `${match.result[left]} - ${match.result[right]}` : "—"}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const buttonState = getMatchButtonState(match);

                      if (buttonState.disabled) {
                        return (
                          <div className="px-4 py-2 rounded font-medium bg-slate-900 text-slate-400">
                            {buttonState.text}
                          </div>
                        );
                      }

                      return (
                        <button
                          onClick={() => {
                            if (tournament?.compiled) {
                              alert("❌ Ce tournoi a déjà été compilé. Vous ne pouvez plus modifier les scores.");
                              return;
                            }
                            if (isNextRoundPlayed(match)) {
                              alert("❌ Impossible de modifier le score. Le tour suivant a déjà été joué.");
                              return;
                            }
                            if (match.result && !isAdmin) {
                              alert("❌ Un score a déjà été entré pour ce match. Seuls les administrateurs peuvent modifier les scores existants.");
                              return;
                            }
                            // Track if we need to swap teams for display (user should always be team1)
                            const shouldSwap = isUserOnTeam(match, "team2");
                            setSwappedMatches(prev => ({ ...prev, [match.id]: shouldSwap }));
                            setEditingMatchId(match.id);
                          }}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded font-medium bg-yellow-600 hover:bg-yellow-700 text-white"
                        >
                          <PenLine size={15} />
                          {buttonState.text}
                        </button>
                      );
                    })()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50 flex items-center justify-center">
        <p>Chargement...</p>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50 flex items-center justify-center">
        <p>Tournoi introuvable.</p>
      </div>
    );
  }

  if (!user?.uid) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50 flex items-center justify-center">
        <p>Vous devez être connecté pour voir vos matchs.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 w-full">
      <div className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        <div>
          <Link
            to={`/locations/${tournament.locationId}`}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 transition mb-4"
          >
            <ArrowLeft size={18} />
            Retour
          </Link>
          <h1 className="text-2xl font-bold">Mes Matchs</h1>
          <h2 className="text-lg text-slate-300">{tournament.name}</h2>
        </div>

        {myMatches.length === 0 ? (
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 text-center">
            <p className="text-slate-300">Vous n'avez aucun match dans ce tournoi.</p>
          </div>
        ) : (
          <div>
            {(() => {
              const { qualification, elimination } = groupMatchesByPhase(myMatches);

              return (
                <>
                  {renderMatchGroup(
                    "Qualification",
                    qualification,
                    qualificationOpen,
                    () => setQualificationOpen(!qualificationOpen),
                    "🏁"
                  )}
                  {renderMatchGroup(
                    "Élimination",
                    elimination,
                    eliminationOpen,
                    () => setEliminationOpen(!eliminationOpen),
                    "🏆"
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Modal */}
        {editingMatchId && (() => {
          const match = matches.find((m) => m.id === editingMatchId);
          const isSwapped = swappedMatches[editingMatchId];

          const currentScore = editingScores[editingMatchId] || {
            team1: match?.result?.team1 ?? "",
            team2: match?.result?.team2 ?? "",
          };

          // Swap display if user is on team2
          const displayScore = isSwapped
            ? { team1: currentScore.team2, team2: currentScore.team1 }
            : currentScore;

          const displayTeam1Name = isSwapped
            ? getTeamDisplayName(match, "team2")
            : getTeamDisplayName(match, "team1");

          const displayTeam2Name = isSwapped
            ? getTeamDisplayName(match, "team1")
            : getTeamDisplayName(match, "team2");

          return (
            <ScoreEditModal
              matchId={editingMatchId}
              score={displayScore}
              onChange={handleScoreChange}
              onCancel={() => {
                setEditingMatchId(null);
                setSwappedMatches(prev => {
                  const updated = { ...prev };
                  delete updated[editingMatchId];
                  return updated;
                });
              }}
              onSave={handleScoreSubmit}
              team1Name={displayTeam1Name}
              team2Name={displayTeam2Name}
              goalsToWin={match?.goalsToWin || 1}
            />
          );
        })()}
      </div>
    </div>
  );
};

export default MyMatches;